import React, { useState } from 'react';

export interface PickerItem {
    id: string | number;
    name: string;
    sub?: string;
}

interface HistoryRow {
    src: string;
    tgt: string;
}

interface ManualPickerProps {
    srcLabel: string;
    tgtLabel: string;
    srcItems: PickerItem[];
    tgtItems: PickerItem[];
    loading?: boolean;
    extraFieldsPanel?: React.ReactNode;
    onMap: (src: PickerItem, tgt: PickerItem) => Promise<void>;
    onError?: (message: string) => void;
    history?: HistoryRow[];
}

export const ManualPicker: React.FC<ManualPickerProps> = ({
    srcLabel, tgtLabel, srcItems, tgtItems, loading = false, extraFieldsPanel, onMap, onError, history = [],
}) => {
    const [srcQuery, setSrcQuery] = useState('');
    const [tgtQuery, setTgtQuery] = useState('');
    const [selSrc, setSelSrc] = useState<PickerItem | null>(null);
    const [selTgt, setSelTgt] = useState<PickerItem | null>(null);
    const [mapping, setMapping] = useState(false);

    const filteredSrc = srcItems.filter(i =>
        !srcQuery || i.name.toLowerCase().includes(srcQuery.toLowerCase()) || (i.sub ?? '').toLowerCase().includes(srcQuery.toLowerCase())
    );
    const filteredTgt = tgtItems.filter(i =>
        !tgtQuery || i.name.toLowerCase().includes(tgtQuery.toLowerCase()) || (i.sub ?? '').toLowerCase().includes(tgtQuery.toLowerCase())
    );

    const canMap = !!(selSrc && selTgt) && !mapping;

    const handleMap = async () => {
        if (!selSrc || !selTgt) return;
        setMapping(true);
        try {
            await onMap(selSrc, selTgt);
            setSelSrc(null);
            setSelTgt(null);
        } catch (err) {
            onError?.(err instanceof Error ? err.message : 'Failed to create mapping');
        } finally {
            setMapping(false);
        }
    };

    return (
        <div className="mapper-ui font-sans text-mapper-text">
            {extraFieldsPanel}

            <div className="bg-mapper-white border border-mapper-border rounded-[15px] overflow-hidden">
                <div className="grid grid-cols-[1fr_64px_1fr]">
                    {/* source */}
                    <div className="border-r border-mapper-border-3">
                        <div className="px-5 pt-[15px] pb-3 border-b border-mapper-border-3">
                            <div className="flex items-center gap-2 mb-[10px]">
                                <span className="w-[7px] h-[7px] rounded-full bg-mapper-purple" />
                                <h4 className="text-[13px] font-bold m-0 text-mapper-body-text">{srcLabel}</h4>
                            </div>
                            <input
                                value={srcQuery}
                                onChange={e => setSrcQuery(e.target.value)}
                                placeholder="Search…"
                                className="w-full px-[11px] py-2 border border-mapper-border-2 rounded-[9px] text-[13px] outline-none bg-mapper-surface-2"
                            />
                        </div>
                        <div className="max-h-[440px] overflow-y-auto p-2">
                            {loading ? (
                                <div className="text-[13px] text-mapper-muted-2 px-3 py-4">Loading…</div>
                            ) : filteredSrc.length === 0 ? (
                                <div className="text-[13px] text-mapper-muted-2 px-3 py-4">No items found</div>
                            ) : (
                                filteredSrc.map(item => (
                                    <button
                                        key={item.id}
                                        onClick={() => setSelSrc(item)}
                                        className={`block w-full text-left px-[13px] py-[11px] rounded-[10px] mb-[3px] border ${
                                            selSrc?.id === item.id ? 'bg-mapper-purple-bg-3 border-mapper-purple-border-3' : 'border-transparent'
                                        }`}
                                    >
                                        <div className="text-[13.5px] font-semibold text-mapper-text">{item.name}</div>
                                        {item.sub && <div className="text-[11.5px] text-mapper-muted-2">{item.sub}</div>}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>

                    {/* connector */}
                    <div className="flex items-center justify-center bg-mapper-surface">
                        <div className="w-8 h-8 rounded-full bg-mapper-white border border-mapper-border-2 flex items-center justify-center text-mapper-muted-3">
                            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M5 12h14M13 6l6 6-6 6" />
                            </svg>
                        </div>
                    </div>

                    {/* target */}
                    <div>
                        <div className="px-5 pt-[15px] pb-3 border-b border-mapper-border-3">
                            <div className="flex items-center gap-2 mb-[10px]">
                                <span className="w-[7px] h-[7px] rounded-full bg-mapper-teal" />
                                <h4 className="text-[13px] font-bold m-0 text-mapper-body-text">{tgtLabel}</h4>
                            </div>
                            <input
                                value={tgtQuery}
                                onChange={e => setTgtQuery(e.target.value)}
                                placeholder="Search…"
                                className="w-full px-[11px] py-2 border border-mapper-border-2 rounded-[9px] text-[13px] outline-none bg-mapper-surface-2"
                            />
                        </div>
                        <div className="max-h-[440px] overflow-y-auto p-2">
                            {loading ? (
                                <div className="text-[13px] text-mapper-muted-2 px-3 py-4">Loading…</div>
                            ) : filteredTgt.length === 0 ? (
                                <div className="text-[13px] text-mapper-muted-2 px-3 py-4">No items found</div>
                            ) : (
                                filteredTgt.map(item => (
                                    <button
                                        key={item.id}
                                        onClick={() => setSelTgt(item)}
                                        className={`block w-full text-left px-[13px] py-[11px] rounded-[10px] mb-[3px] border ${
                                            selTgt?.id === item.id ? 'bg-mapper-teal-bg-3 border-mapper-teal-border-3' : 'border-transparent'
                                        }`}
                                    >
                                        <div className="text-[13.5px] font-semibold text-mapper-text">{item.name}</div>
                                        {item.sub && <div className="text-[11.5px] text-mapper-muted-2">{item.sub}</div>}
                                    </button>
                                ))
                            )}
                        </div>
                    </div>
                </div>

                {/* footer */}
                <div className="border-t border-mapper-border-3 px-5 py-[14px] flex items-center justify-between gap-4 bg-mapper-surface">
                    <div className="text-[13px] text-mapper-muted min-w-0">
                        {selSrc && selTgt ? (
                            <>Mapping <strong className="text-mapper-text">{selSrc.name}</strong> → <strong className="text-mapper-teal-hover">{selTgt.name}</strong></>
                        ) : (
                            'Select one item on each side, then map them.'
                        )}
                    </div>
                    <button
                        onClick={handleMap}
                        disabled={!canMap}
                        className={`flex items-center gap-2 px-5 py-[10px] rounded-[10px] text-[13.5px] font-bold shrink-0 ${
                            canMap ? 'bg-mapper-teal text-white shadow-mapper-btn' : 'bg-mapper-gray-bg text-mapper-muted-5 cursor-default'
                        }`}
                    >
                        <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                            <circle cx="6" cy="6" r="2.2" /><circle cx="18" cy="18" r="2.2" /><path d="M8.2 6H14a4 4 0 0 1 4 4v5.6" />
                        </svg>
                        {mapping ? 'Mapping…' : 'Map selected'}
                    </button>
                </div>
            </div>

            {history.length > 0 && (
                <div className="mt-[26px]">
                    <h3 className="text-[14px] font-bold m-0 mb-3 flex items-center gap-2">
                        <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#1F9D6B" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round">
                            <path d="M20 6 9 17l-5-5" />
                        </svg>
                        Mapped this session
                        <span className="font-mono text-xs bg-mapper-teal-bg-3 text-mapper-teal-dark px-[9px] py-[1px] rounded-full">{history.length}</span>
                    </h3>
                    <div className="bg-mapper-white border border-mapper-border rounded-[13px] overflow-hidden">
                        {history.map((h, i) => (
                            <div key={i} className="flex items-center gap-[14px] px-[18px] py-3 border-b border-mapper-border-4 text-[13.5px] last:border-b-0">
                                <span className="font-semibold text-mapper-text flex-1">{h.src}</span>
                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#C2C9D1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M5 12h14M13 6l6 6-6 6" />
                                </svg>
                                <span className="font-semibold text-mapper-teal-hover flex-1">{h.tgt}</span>
                                <span className="text-[11px] font-bold px-[10px] py-[3px] rounded-full bg-mapper-teal-bg-3 text-mapper-teal-dark">Manual</span>
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
};
